import React from 'react';
import ThreeDotsLoader from '@/components/common-components/Loaders/ThreeDotsLoader';
import XIcon from '@/components/Icons/icons/XIcon';
import { FeatureKeys } from '@/hooks/useFeature';
import { signOut } from '@/services/list.service';
import { getMagicLinkEmailHtml } from '@/utils/sathi-utils';
import AttachMoney from '@mui/icons-material/AttachMoney';
import CurrencyFrancIcon from '@mui/icons-material/CurrencyFranc';
import CurrencyPoundIcon from '@mui/icons-material/CurrencyPound';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import CurrencyYenIcon from '@mui/icons-material/CurrencyYen';
import CurrencyYuanIcon from '@mui/icons-material/CurrencyYuan';
import EuroIcon from '@mui/icons-material/Euro';
import { formatRefUrl } from '@Saral-Influencer-Marketing/saral-utils/dist/functions/profiles/socialLinks.js';
import * as Sentry from '@sentry/react';
import Gleap from 'gleap';
import Cookies from 'js-cookie';
import posthog from 'posthog-js';
import { NotificationManager } from 'react-notifications';
import { useDispatch } from 'react-redux';
import blackBelt from '../components/Icons/icons/Gamification/black-belt.svg';
import brownBelt from '../components/Icons/icons/Gamification/brown-belt.svg';
import purpleBelt from '../components/Icons/icons/Gamification/purple-belt.svg';
import yellowBelt from '../components/Icons/icons/Gamification/yellow-belt.svg';
import instagramLogo from '../images/platforms/instagram.svg';
import shortsLogo from '../images/platforms/shorts.svg';
import tiktokLogo from '../images/platforms/tiktok.svg';
import websiteLogo from '../images/platforms/website.svg';
import youtubeLogo from '../images/platforms/youtube.svg';
import { getStorage, removeStorage, setStorage } from './auth-utils';
import { CURRENT_TEAMMEMBER_LOGGED_KEY, isJsrStorageKey, SWITCH_ID_KEY } from './constants';
import { API_STATUS } from './constants/api';
import { isPulseEnabled } from './feature-flags';
import { clearAllCache } from './indexedDB';
import { languageMap } from './languageTranslations';
import { Mixpanel } from './mixpanel';


export const getPlatformImage = (platform, profileLink = '') => {
  platform = platform?.toLowerCase();
  let p;
  if (platform?.includes('instagram') || platform?.includes('ig') || platform?.includes('insta')) {
    p = 'instagram';
  } else if (platform?.includes('youtube') || platform?.includes('yt')) {
    p = 'youtube';
  } else if (platform?.includes('tiktok') || platform?.includes('tt')) {
    p = 'tiktok';
  } else if (platform?.includes('website')) {
    p = 'website';
  } else if (platform?.includes('shorts')) {
    p = 'shorts';
  }

  if (!p && profileLink) {
    if (profileLink?.includes('instagram') || profileLink?.includes('ig') || profileLink?.includes('insta')) {
      p = 'instagram';
    } else if (profileLink?.includes('youtube') || profileLink?.includes('yt')) {
      p = 'youtube';
    } else if (profileLink?.includes('tiktok') || profileLink?.includes('tt')) {
      p = 'tiktok';
    }
  }
  switch (p) {
    case 'instagram':
      return instagramLogo;
    case 'youtube':
      return youtubeLogo;
    case 'tiktok':
      return tiktokLogo;
    case 'website':
      return websiteLogo;
    case 'shorts':
      return shortsLogo;
    default:
      return instagramLogo;
  }
};

export const getPlatformFromUrl = (url) => {
  url = url?.toLowerCase();
  if (url?.includes('instagram') || url?.includes('ig') || url?.includes('insta')) {
    return 'instagram';
  } else if (url?.includes('youtube') || url?.includes('yt')) {
    return 'youtube';
  } else if (url?.includes('tiktok') || url?.includes('tt')) {
    return 'tiktok';
  } else {
    return 'instagram';
  }
};

export const platformFromUrl = (platform) => {
  platform = platform.toLowerCase();
  if (platform.includes('instagram') || platform.includes('ig') || platform.includes('insta')) {
    return 'instagram';
  } else if (platform.includes('youtube') || platform.includes('yt')) {
    return 'youtube';
  } else if (platform.includes('tiktok') || platform.includes('tt')) {
    return 'tiktok';
  } else {
    return 'instagram';
  }
};

export const numFormatter = (num) => {
  if (num > 999 && num < 1000000) {
    return (num / 1000).toFixed(1) + 'K'; // convert to K for number from > 1000 < 1 million
  } else if (num > 1000000) {
    return (num / 1000000).toFixed(1) + 'M'; // convert to M for number from > 1 million
  } else if (num < 1000) {
    return num; // if value < 1000, nothing to do
  }
};

export const isValidUrl = (urlString) => {
  var urlPattern = new RegExp(
    '^(https?:\\/\\/)?' + // validate protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
      '(\\:\\d+)?' + // validate port
      '(\\/[-a-z\\d%_.~+@]*)*' + // validate path and allow @
      '(\\?[;&a-z\\d%_.~+=-@]*)?' + // validate query string and allow @
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // validate fragment locator
  return !!urlPattern.test(urlString);
};

export const getSubdomainName = (url) => {
  if (url.includes('http')) {
    url = url?.split('//')[1];
  }
  const subdomainName = url?.split('.');
  if (url.includes('www')) {
    const match = url.match(partialUrlRegex);
    if (match) {
      return match[1];
    }
  }
  const match = url.match(secondLevelDomain);
  if (match) {
    return match[1];
  }
  if (url.includes('.co.')) {
    return subdomainName[subdomainName.length - 3];
  } else {
    return subdomainName[subdomainName.length - 2];
  }
};

export const formatFollowers = (num) => {
  function intlFormat(num) {
    return new Intl.NumberFormat().format(Math.round(num * 10) / 10);
  }
  if (num >= 1000000) return intlFormat(num / 1000000) + 'M';
  if (num >= 1000) return intlFormat(num / 1000) + 'k';
  return intlFormat(num);
};

export const formatNumber = (num, round = 3, decimalPlaces = 2) => {
  if (num === 0 || !num) return 0;
  num = parseFloat(num).toFixed(round);
  function intlFormat(num) {
    if (num > 0) {
      // round to specified decimal places
      const multiplier = Math.pow(10, decimalPlaces);
      return new Intl.NumberFormat().format(Math.round(num * multiplier) / multiplier);
    }
    return num;
  }
  if (num >= 1000000) return intlFormat(num / 1000000) + 'M';
  if (num >= 1000) return intlFormat(num / 1000) + 'K';
  return intlFormat(num);
};
export const formatNumberWithRounding = (num, round = 3) => {
  if (num === 0 || !num) return 0;
  num = parseFloat(num).toFixed(round);
  function intlFormat(num) {
    if (num > 0) {
      // For numbers over 100K, round to nearest whole number
      if (num >= 100) {
        return new Intl.NumberFormat().format(Math.round(num));
      }
      // For smaller numbers, round to 2 decimal places
      return new Intl.NumberFormat().format(Math.round(num * 100) / 100);
    }
    return num;
  }
  if (num >= 1000000) return intlFormat(num / 1000000) + 'M';
  if (num >= 1000) return intlFormat(num / 1000) + 'K';
  return intlFormat(num);
};

export const formatFairFeeRange = (min, max, currencyLogo) => {
  return `${currencyLogo}${formatNumber(min, 1, 1)} - ${currencyLogo}${formatNumber(max, 1, 1)}`;
};

export const getMinutesDifference = (startDate, endDate) => {
  const difference = startDate.getTime() - endDate.getTime();
  const resultInMinutes = Math.round(difference / 60000);
  return resultInMinutes;
};

export const getDaysDifference = (date1, date2) => {
  const differenceInMilliseconds = date2 - date1;
  const millisecondsInOneDay = 1000 * 60 * 60 * 24;
  const differenceInDays = differenceInMilliseconds / millisecondsInOneDay;
  return Math.abs(differenceInDays);
};

export const isValidURL = (url) => {
  var res = url.match(
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g
  );
  return res !== null;
};

export const calculateERFlag = (eng_rate) => {
  if (eng_rate >= 0 && eng_rate <= 0.29) {
    return { color: '#E64848', flag: 'Avoid' };
  } else if (eng_rate >= 0.3 && eng_rate <= 0.49) {
    return { color: '#FF7276', flag: 'Meh' };
  } else if (eng_rate >= 0.5 && eng_rate < 3) {
    return { color: '#F2D600', flag: 'Good' };
  } else if (eng_rate >= 3 && eng_rate <= 5) {
    return { color: '#12B829', flag: 'Great' };
  } else if (eng_rate > 5 && eng_rate <= 80) {
    return { color: '#7357E9', flag: 'Excellent' };
  } else if (eng_rate > 80) {
    return { color: '#FF5733', flag: 'Viral' };
  }
};

export const calculateCLRatioFlag = (cl_ratio) => {
  if (cl_ratio >= 0 && cl_ratio <= 0.01) {
    return { color: '#FF7276', flag: 'Meh' };
  } else if (cl_ratio > 0.001 && cl_ratio <= 0.1) {
    return { color: '#12B829', flag: 'Good' };
  } else if (cl_ratio > 0.1 && cl_ratio <= 0.25) {
    return { color: '#7357E9', flag: 'Amazing' };
  } else if (cl_ratio > 0.25) {
    return { color: '#ffdf00', flag: 'Crazy' };
  }
};

export const formatFairFee = (fair_fee) => {
  if (fair_fee === 0) return 'N/A';

  if (fair_fee > 25000) {
    return '$25k+';
  } else if (fair_fee < 100) {
    return '$100';
  } else if (fair_fee < 1000) {
    return '$' + formatFollowers(fair_fee.toFixed(0));
  } else {
    return '$' + formatFollowers(fair_fee.toFixed(2));
  }
};

export const instaHostRegex = /(?:(?:http|https):\/\/)?(?:www.)?(?:instagram)(?:.com\/)/im;
export const instaUsernameRegex =
  /(?:(?:http|https):\/\/)?(?:www\.)?(?:instagram\.com|instagr\.am)\/([A-Za-z0-9-_\.]+)/im;
export const ytHostRegex = /(?:(?:http|https):\/\/)?(?:www.)?(?:youtube)(?:.com)/im;
export const ytUsernameRegex =
  /(?:(?:https|http):\/\/)?(?:www.)?(?:youtube)(?:.com\/((c)\/[A-Za-z0-9-_\.]+|(channel)\/[A-Za-z0-9-_\.]+|(user)\/[A-Za-z0-9-_\.]+|[A-Za-z0-9-_\.@]+))/im;
export const ttHostRegex = /(?:(?:http|https):\/\/)?(?:www.)?(?:tiktok)(?:.com)/im;
export const ttUsernameRegex = /(?:(?:https|http):\/\/)?(?:www.)?(?:tiktok)(?:.com)\/(@)([A-Za-z0-9-_\.]+)/im;

export const isValidProfileUrl = (url) => {
  if (url?.includes('instagram') || url?.includes('tiktok') || url?.includes('youtube')) {
    return true;
  }
  return false;
};

export const isValidPlatformProfileLink = (url, platform) => {
  switch (platform) {
    case 'youtube':
      return url?.includes('youtube');
    case 'instagram':
      return url?.includes('instagram');
    case 'tiktok':
      return url?.includes('tiktok');
    case 'website':
      // Validate for a proper URL structure
      return /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-]*)*\/?$/.test(url);
    default:
      return false;
  }
};

export const parseUsername = (url, type) => {
  if (type === 'tt') {
    const res = ttUsernameRegex.exec(url);
    return res.length > 2 ? res[2] : null;
  }
  var result = instaUsernameRegex.exec(url);
  return result.length > 1 ? result[1] : null;
};

export const getShippingStatus = (status) => {
  let result = [null, null];
  if (!status) {
    return ['To be fulfilled', '#667085'];
  }
  if (status === 'inforeceived' || status === 'InfoReceived') {
    result[0] = 'Shipped';
    result[1] = '#ffd97a';
    return result;
  }
  switch (status) {
    case 'inforeceived': {
      result[0] = 'Shipped';
      result[1] = '#ffd97a';
      break;
    }
    case 'transit': {
      result[0] = 'In Transit';
      result[1] = '#7357e9';
      break;
    }
    case 'pickup': {
      result[0] = 'Delivery Soon';
      result[1] = '#ff7d20';
      break;
    }
    case 'delivered': {
      result[0] = 'Delivered';
      result[1] = '#12b829';
      break;
    }
    default:
      result[0] = status;
      result[1] = '#667085';
      break;
  }
  return result;
};

export const getEmail = (contacts) => {
  const email = contacts?.find((contact) => contact?.type === 'email');
  return email?.value;
};

export const getFollowersFilterName = (value) => {
  let name = '';
  switch (value) {
    case '3000-20000':
      name = 'Nano';
      break;
    case '20000-100000':
      name = 'Micro';
      break;
    case '100000-500000':
      name = 'Macro';
      break;
    case '500000-1000000':
      name = 'Mega';
      break;
    case '1000000-10000000':
      name = 'Whale';
      break;
    default:
      break;
  }
  return name;
};

let timer;
export const debounce = (func, timeout = 300) => {
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
};

export const throttle = (cb, delay = 300) => {
  let wait = false;
  return (...args) => {
    if (wait) {
      return;
    }
    cb(...args);
    wait = true;
    setTimeout(() => {
      wait = false;
    }, delay);
  };
};

export const getDomainName = (url) => {
  if (url.includes('http')) {
    url = url?.split('//')[1];
  }
  const subdomainName = url?.split('.');
  return subdomainName[subdomainName.length - 2];
};

export const removeNumFormat = (val) => {
  let returnVal;
  const multiplier = val.substr(-1).toLowerCase();
  if (multiplier === 'k') returnVal = parseFloat(val) * 1000;
  else if (multiplier === 'm') returnVal = parseFloat(val) * 1000000;
  else if (multiplier === 'b') returnVal = parseFloat(val) * 1000000000;
  return parseInt(returnVal);
};

export const getFileExtentionFromLink = (link) => {
  const d = link?.split('.');
  if (d[d.length - 1].includes('%')) {
    return d[d.length - 1]?.split('%')[0];
  } else {
    return d[d.length - 1];
  }
};

export const calculateTimeDifferenceInMinutes = (date1, date2) => {
  const diffInMilliseconds = Math.abs(date2 - date1);
  const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
  return diffInMinutes;
};

export const formatCurrency = (currencyAmount, currencyCode = 'USD') => {
  if (!currencyAmount) return 0;
  currencyAmount = Number(currencyAmount);
  const formattedCurrency = currencyAmount.toLocaleString('en-US', {
    style: 'currency',
    currency: currencyCode,
  });
  return formattedCurrency;
};

export const hasQueryParams = (url) => {
  const searchParams = new URL(url).searchParams;
  return searchParams && searchParams.toString().trim() !== '';
};

export function checkForQueryParams(url) {
  const searchParams = new URL(url).searchParams;
  let hasQueryParams = false;
  let hasRefQueryParam = false;

  if (searchParams.toString().length) hasQueryParams = true;
  if (searchParams.has('ref')) hasRefQueryParam = true;

  return { hasQueryParams, hasRefQueryParam };
}

export const shortString = (str, length = 24) => {
  if (!str) {
    return 'N/A';
  }
  if (str.length > length) {
    return str.slice(0, length) + '...';
  } else {
    return str;
  }
};

export const stripEmoji = (string) => {
  if (string && string.length > 0) {
    string = string.replace(
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
      ''
    );
    string = string
      .replace(/[^\w\s-]|_/g, '')
      .replace(/\s+/g, ' ')
      .replace(/^[\s-]+|[\s-]+$/g, '');
  }

  return string || '';
};

export const getDateNDaysAgo = (numOfDays, date = new Date()) => {
  const daysAgo = new Date(date.getTime());
  daysAgo.setDate(date.getDate() - numOfDays);
  return daysAgo;
};

export const getDateNDaysAhead = (numOfDays, date = new Date()) => {
  const daysAgo = new Date(date.getTime());
  daysAgo.setDate(date.getDate() + numOfDays);
  return daysAgo;
};

export const getCurrencyLogo = (currencyCode) => {
  const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    AUD: 'A$',
    CAD: 'C$',
    CHF: 'Fr',
    CNY: '¥',
    SEK: 'kr',
    NZD: 'NZ$',
    INR: '₹',
  };
  if (currencyCode in currencySymbols) {
    return currencySymbols[currencyCode];
  } else {
    return currencyCode;
  }
};

export const getCurrencyIcon = (currencyCode = 'USD') => {
  const currencySymbols = {
    USD: <AttachMoney />,
    EUR: <EuroIcon />,
    GBP: <CurrencyPoundIcon />,
    JPY: <CurrencyYenIcon />,
    AUD: <AttachMoney />,
    CAD: <AttachMoney />,
    CHF: <CurrencyFrancIcon />,
    CNY: <CurrencyYuanIcon />,
    SEK: 'kr',
    NZD: 'NZ$',
    INR: <CurrencyRupeeIcon />,
  };
  if (currencyCode in currencySymbols) {
    return currencySymbols[currencyCode];
  } else {
    return currencyCode;
  }
};

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const stripHtmlTags = (htmlString) => {
  return htmlString.replace(/<[^>]*>/g, '');
};
export const capitalizeFirstLetter = (string) => {
  if (!string) {
    return '';
  }
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const emailRegex = new RegExp(
  /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
);

export const passwordRegex = new RegExp(/^(?=.*[A-Z])(?=.*\d).{6,}$/);
export const lightWeightDashboardPasswordRegex = new RegExp(/^.{6,}$/);

export const urlRegex = new RegExp(/^(https?:\/\/)?([a-zA-Z0-9_-]+\.)+[a-zA-Z]{2,63}(\/.*)?$/i);

export const tempEmailRegex = new RegExp(/\.temp@gmail\.com/);

export const secondLevelDomain = new RegExp(/^([^.]*)\.[^.]+\.[^.]+$/);

export const fullUrlRegex = new RegExp(/https?:\/\/www\.(.*?)\.([a-z]{2,})/);

export const partialUrlRegex = new RegExp(/www\.(.*?)\.([a-z]{2,})/);

export const instagramUrlRegex = new RegExp(/^(?:https?:\/\/)?(?:www\.)?(?:instagram\.com|instagr\.am)\/.*$/i);

export const youtubeUrlRegex = new RegExp(/^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)\/.*$/i);

export const tiktokRegex = new RegExp(/^(?:https?:\/\/)?(?:www\.)?(?:tiktok\.com\/|vt\.tiktok\.com\/)([\w-]+)$/i);

export const userFirstAndLastNameRegex = new RegExp(/[\p{L}]/u);

export const brTagRegex = new RegExp(/(<|&lt;)br\s*\/*(>|&gt;)/g);
// check if a subdomain is valid subdomain or not
const subdomainRegex = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,32}[a-zA-Z0-9])?$/;
export const isValidSubdomain = (subdomain) => {
  return subdomainRegex.test(subdomain);
};

export const getPrimaryEmail = (connectedEmailList) => {
  if (connectedEmailList?.length > 0) {
    const primaryEmail = connectedEmailList.find((email) => email?.isPrimary === true);
    if (primaryEmail) {
      return primaryEmail?.email;
    } else {
      return connectedEmailList[0]?.email;
    }
  }
};

export const removeHtmlTags = (text, addSpaceForBreak = false) => {
  if (addSpaceForBreak) {
    return text
      .replace(/<\/(div|p|h[1-6]|li|tr|td|th|blockquote)>/gi, ' ')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<.*?>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  return text.replace(/<.*?>/g, '');
};

export const formatUsername = (username) => {
  if (username && username.length > 0) {
    if (!username.startsWith('@')) {
      username = `@${username}`;
    }
  }
  return username || '';
};

export const formatPlatform = (platform) => {
  if (platform && platform.length > 0) {
    platform = platform.charAt(0).toUpperCase() + platform.slice(1);
  }
  return platform || '';
};

export const formatUrl = (url, formatting) => {
  let formattedUrl = url;
  let filters = ['http://', 'https://'];
  if (formatting === 'removeProtocol') {
    filters.forEach((filter) => {
      if (formattedUrl.includes(filter)) {
        formattedUrl = formattedUrl.replaceAll(filter, '');
        return;
      }
    });
  } else if (formatting === 'addProtocol') {
    if (!formattedUrl?.split(':')[0].includes('http')) {
      formattedUrl = `https://${formattedUrl}`;
    }
  }

  return formattedUrl;
};

export const lazyRetry = function (componentImport, name) {
  return new Promise((resolve, reject) => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(window.sessionStorage.getItem(`retry-${name}-refreshed`) || 'false');
    // try to import the component
    componentImport()
      .then((component) => {
        window.sessionStorage.setItem(`retry-${name}-refreshed`, 'false'); // success so reset the refresh
        resolve(component);
      })
      .catch((error) => {
        if (!hasRefreshed) {
          // not been refreshed yet
          window.sessionStorage.setItem(`retry-${name}-refreshed`, 'true'); // we are now going to refresh
          return window.location.reload(); // refresh the page
        }
        reject(error); // Default error behaviour as already tried refresh
      });
  });
};

export const formatRefLink = (refLink, refCode, utmParams = {}, profile = {}) => {
  if (refLink?.length < 1) {
    return '';
  }
  if (refLink?.includes('gtsrl.io')) {
    return refLink;
  }

  const newRefLink = refLink?.includes('http') ? refLink?.split('//')[1] : refLink;
  const { hasRefQueryParam, hasQueryParams } = checkForQueryParams('https://' + refLink);

  let displayRefLink = '';

  if (hasQueryParams) {
    if (hasRefQueryParam) displayRefLink = newRefLink;
    else displayRefLink = `${newRefLink}&ref=${refCode}`;
  } else {
    displayRefLink = `${newRefLink}?ref=${refCode}`;
  }
  const utmParamsExist = hasUtmParams(displayRefLink);
  if (utmParams && !utmParamsExist) {
    if (utmParams?.utm_source) {
      displayRefLink += `&utm_source=${utmParams?.utm_source}`;
    }
    if (utmParams?.utm_medium) {
      displayRefLink += `&utm_medium=${utmParams?.utm_medium}`;
    }
    if (utmParams?.utm_campaign) {
      displayRefLink += `&utm_campaign=${utmParams?.utm_campaign}`;
    }
  }
  if (displayRefLink?.includes('{{platform}}') || displayRefLink?.includes('{{username}}')) {
    displayRefLink = displayRefLink
      .replaceAll('{{platform}}', profile?.platform || '')
      .replaceAll('{{username}}', profile?.user_name || '');
  }
  return formatRefUrl(displayRefLink);
};

export const getWordCount = (html) => {
  // Remove HTML tags and split the text into words
  const textWithoutTags = html.replace(/(<([^>]+)>)/gi, '');
  const words = textWithoutTags?.split(/\s+/);

  // Filter out empty strings (e.g., consecutive spaces)
  const nonEmptyWords = words.filter((word) => word.trim() !== '');

  return nonEmptyWords.length;
};

export const setStorageObject = (key, jsonObject, storageType = 'local') => {
  try {
    // Convert the JSON object to a string
    const jsonString = JSON.stringify(jsonObject);

    // Store the string in local storage with the specified key
    if (storageType === 'local') {
      localStorage.setItem(key, jsonString);
    } else if (storageType === 'session') {
      sessionStorage.setItem(key, jsonString);
    }
  } catch (error) {
    // Handle any errors that occur during JSON serialization
    console.error('Error storing JSON object:', error);
  }
};

export const updateStorageObject = (key, keyInObject, updatedValue, storageType = 'local') => {
  try {
    const currentObject = getStorageObject(key, storageType) || {};

    currentObject[keyInObject] = updatedValue;

    // Convert the JSON object to a string
    const jsonString = JSON.stringify(currentObject);

    // Store the string in local storage with the specified key
    if (storageType === 'local') {
      localStorage.setItem(key, jsonString);
    } else if (storageType === 'session') {
      sessionStorage.setItem(key, jsonString);
    }
  } catch (error) {
    // Handle any errors that occur during JSON serialization
    console.error('Error storing JSON object:', error);
  }
};

export const getStorageObject = (key, storageType = 'local') => {
  try {
    // Retrieve the string from local storage
    let jsonString = null;
    if (storageType === 'local') {
      jsonString = localStorage.getItem(key);
    } else if (storageType === 'session') {
      jsonString = sessionStorage.getItem(key);
    }

    // If the string is null or undefined, return null
    if (jsonString === null || jsonString === undefined) {
      return null;
    }

    // Parse the JSON string to a JavaScript object
    const jsonObject = JSON.parse(jsonString);

    return jsonObject;
  } catch (error) {
    // Handle any errors that occur during JSON parsing
    console.error('Error retrieving JSON object:', error);
    return null;
  }
};

export const removeDuplicatesFromArray = (accounts, keyMapper) => {
  let duplicateCount = 0;
  if (!accounts) return { arr: accounts, duplicateCount };

  const uniqueArray = accounts.reduce((accumulator, currentObject) => {
    // Use a Map to track objects by their 'id' property
    if (!accumulator.has(keyMapper(currentObject))) {
      accumulator.set(keyMapper(currentObject), currentObject);
    } else {
      duplicateCount += 1;
    }
    return accumulator;
  }, new Map());

  // Convert the Map back to an array of objects
  return { arr: Array.from(uniqueArray.values()), duplicateCount };
};

const allowedCustomParams = [
  '{{first_name}}',
  '{{user_name}}',
  '{{custom_line}}',
  '{{platform}}',
  '{{ai_personal_line}}',
];

export const sanitizeInvalidCustomParams = (email) => {
  const pattern = /{{(.*?)}}/g;
  const newEmail = email.replaceAll('&amp;', '&');

  return newEmail.replace(pattern, (match, group) => {
    if (!allowedCustomParams.includes(match)) {
      return group;
    }
    return match;
  });
};

export const extractUsernameFromLink = (link) => {
  let username = null;
  if (link.includes('instagram')) {
    username = link && link?.includes('http') ? link?.split('/')[3] : link?.split('/')[1];
  } else if (link.includes('tiktok')) {
    username = link && link?.includes('http') ? link?.split('/')[3] : link?.split('/')[1];
  } else if (link.includes('youtube')) {
    if (link.includes('/channel/') || link.includes('/user/') || link.includes('/c/')) {
      username = link && link?.includes('http') ? link?.split('/')[4] : link?.split('/')[2];
    } else {
      username = link && link?.includes('http') ? link?.split('/')[3] : link?.split('/')[1];
    }
  }
  return username;
};
export const hexColorRegex = new RegExp(/^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/);

export const triggerGTMViewPageEvent = (pageName) => {
  Mixpanel.track('page_viewed', { 'Page Name': pageName });
};

export const addHttpsIfNotPresent = (val) => {
  if (!val?.length) return val;

  if (!val.includes('http')) return `https://${val}`;

  return val;
};

export const triggerGTMCtaClickedEvent = (pageName, action) => {
  window.dataLayer.push({
    event: 'CTAClicked',
    custom_parameter_page_name: pageName,
    custom_parameter_action: action,
  });
};

export const triggerCoreEvent = ({ platform, intent, manual, recommended, filters, recent }) => {
  window.dataLayer.push({
    event: 'CreatorSearchPerformed',
    custom_parameter_platform: platform,
    custom_parameter_intent: intent,
    custom_parameter_manual: manual,
    custom_parameter_recommended: recommended,
    custom_parameter_filters_applied: filters,
    custom_parameter_recent: recent,
  });
};

export const triggerCoreEventCampaignCreated = ({ pageName, emailType }) => {
  window.dataLayer.push({
    event: 'EmailCampaignCreated',
    custom_parameter_page_name: pageName,
    custom_parameter_email_type: emailType,
  });
};

export const triggerCoreEventPayoutsCompleted = ({ pageName, payoutType }) => {
  window.dataLayer.push({
    event: 'PayoutCompleted',
    custom_parameter_page_name: pageName,
    custom_parameter_email_type: payoutType,
  });
};

export const triggerCoreAction = ({ pageName, actionType, creatorListSize = '' }) => {
  window.dataLayer.push({
    event: 'SaralCoreActionPerformed',
    custom_parameter_page_name: pageName,
    custom_parameter_action_type: actionType,
    creatorListSize: creatorListSize,
  });
};

export const checkIfTiktokShareLink = (link) => {
  return link?.includes('https://www.tiktok.com/share/user');
};

export const checkIfTiktokUsernameIsNumbers = (val) => {
  let username = val;
  if (username.includes('@')) {
    username = username.replace('@', '');
  }
  return /^\d+$/.test(username);
};

export const getUsernameFromLink = (link) => {
  return link?.includes('http') ? link?.split('/')[3] : link?.split('/')[1];
};

export const getFormattedIntelligentName = ({ profile }) => {
  let firstName = profile?.first_name;

  if (!firstName) {
    const nameParts = profile?.name?.split(' ') || [];
    firstName = firstName || nameParts[0];
  }

  let intelligentName = firstName;
  if (profile?.intelligentFirstname && profile?.intelligentNamingCheck) {
    intelligentName = profile?.intelligentFirstname;
  }
  return intelligentName;
};

export const formatCustomParamsInEmails = ({
  profile,
  userDetails,
  brandName,
  affiliateDetails,
  emailTemplate,
  utmParams,
  magicLink = '',
}) => {
  // Extract first and last name from `name` field if they are missing

  let lastName = profile?.last_name;

  if (!lastName) {
    const nameParts = profile?.name?.split(' ') || [];
    lastName = lastName || nameParts.slice(1).join(' ');
  }

  return emailTemplate
    ?.replaceAll('{{first_name}}', getFormattedIntelligentName({ profile }))
    ?.replaceAll('{{last_name}}', lastName || '')
    ?.replaceAll('{{user_name}}', formatUsername(profile?.user_name))
    ?.replaceAll('{{custom_line}}', profile?.custom_line || '')
    ?.replaceAll('{{ai_personal_line}}', profile?.custom_line || '')
    ?.replaceAll('{{custom_parameter1}}', profile?.custom_parameters?.[0] || '')
    ?.replaceAll('{{custom_parameter2}}', profile?.custom_parameters?.[1] || '')
    ?.replaceAll('{{your_name}}', userDetails?.first_name || 'Your new friend')
    ?.replaceAll('{{brand_name}}', brandName || '')
    ?.replaceAll('{{platform}}', formatPlatform(profile?.platform) || '')
    ?.replaceAll(
      '{{tracking_link}}',
      affiliateDetails?.shortLinkStatus
        ? affiliateDetails?.shortLink || ''
        : affiliateDetails?.refLink || affiliateDetails?.referralCustomLinks?.[0] || ''
    )
    ?.replaceAll(
      '{{discount_code}}',
      affiliateDetails?.dynamicDiscountEnabled
        ? 'Auto-generated on link click'
        : affiliateDetails?.coupons?.[0]?.code
          ? affiliateDetails?.coupons?.[0]?.code
          : ''
    )
    ?.replaceAll('{{dashboard_link}}', affiliateDetails?.creatorDetails?.dashboardLink || '')
    ?.replaceAll('{{login_email}}', affiliateDetails?.creatorDetails?.loginEmail || '')
    ?.replaceAll('{{password}}', affiliateDetails?.creatorDetails?.password || '')
    ?.replaceAll('{{magic_link}}', getMagicLinkEmailHtml(magicLink));
};

export const formatCustomParamsInUsageRights = ({ template, name, username }) => {
  return template?.replaceAll('{{first_name}}', name)?.replaceAll('{{user_name}}', username || '');
};

export const formatValue = (value, returnZero = false) => {
  if (value <= 0) {
    return returnZero ? 0 : 1000;
  } else if (value < 1000) {
    return 1000;
  } else if (value <= 500000) {
    return value;
  } else if (value <= 750000) {
    return (value - 500000) * 2 + 500000;
  } else {
    return (value - 750000) * 16 + 1000000;
  }
};

export const reverseValue = (formattedValue) => {
  if (formattedValue <= 1000) {
    return 0;
  } else if (formattedValue <= 500000) {
    return formattedValue;
  } else if (formattedValue <= 1000000) {
    return (formattedValue - 500000) / 2 + 500000;
  } else {
    return (formattedValue - 1000000) / 16 + 750000;
  }
};

export const lightenColor = ({ hexCode, percent = 10 }) => {
  // Remove the '#' at the beginning
  hexCode = hexCode.substring(1);

  // Convert hex to RGB
  let r = parseInt(hexCode.substring(0, 2), 16);
  let g = parseInt(hexCode.substring(2, 4), 16);
  let b = parseInt(hexCode.substring(4, 6), 16);

  // Lighten RGB values
  r = Math.min(255, Math.round(r * (1 + percent / 100)));
  g = Math.min(255, Math.round(g * (1 + percent / 100)));
  b = Math.min(255, Math.round(b * (1 + percent / 100)));

  // Convert back to hex
  const newHex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`;

  return newHex;
};

export const hasUtmParams = (url) => {
  if (url?.includes('utm_medium') || url?.includes('utm_source') || url?.includes('utm_campaign')) {
    return true;
  }
  return false;
};

export const getCtaOptionsFromLanguage = (language) => {
  const options = [
    { label: 'Apply Now!', value: 'Apply Now!', isApplyNow: true, isSubmit: false },
    { label: 'Sign me up!', value: 'Sign me up!', isApplyNow: false, isSubmit: false },
    { label: 'Submit!', value: 'Submit!', isApplyNow: false, isSubmit: true },
    { label: 'See if I Qualify', value: 'See if I Qualify', isApplyNow: false, isSubmit: false },
    { label: 'Let me in!', value: 'Let me in!', isApplyNow: false, isSubmit: false },
    { label: 'Join Now!', value: 'Join Now!', isApplyNow: false, isSubmit: false },
  ];

  return options.map((option) => {
    if (option.value === 'Apply Now!') {
      return {
        label: languageMap[language].applyNowCta,
        value: languageMap[language].applyNowCta,
        isApplyNow: true,
        isSubmit: false,
      };
    }
    if (option.value === 'Submit!') {
      return {
        label: languageMap[language]?.submitCta,
        value: languageMap[language]?.submitCta,
        isApplyNow: false,
        isSubmit: true,
      };
    }
    return option;
  });
};

export const formatTime = (time) => {
  const sec_num = parseInt(time, 10); // don't forget the second param
  const hours = Math.floor(sec_num / 3600);
  const minutes = Math.floor((sec_num - hours * 3600) / 60);
  const seconds = sec_num - hours * 3600 - minutes * 60;

  const formattedHours = hours > 0 ? `${hours}:` : '';
  const formattedMinutes = `${minutes}:`;
  const formattedSeconds = `${seconds.toString().padStart(2, '0')}`;

  return `${formattedHours}${formattedMinutes}${formattedSeconds}`;
};

export const copyToClipboard = ({ text, onSuccess, onError }) => {
  navigator?.clipboard?.writeText(text).then(onSuccess).catch(onError);
};

export const downloadFiles = async (fileUrl) => {
  const link = document.createElement('a');
  link.href = fileUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const BASIC_DISCOVERY_FILTERS = ['email', 'engagement', 'followers', 'gender', 'location'];

export const ADVANCED_DISCOVERY_FILTERS = ['accountType', 'age', 'authenticity', 'avgReelsPlays', 'last_posted'];

export const customRound = (number) => {
  // Determine the last digit of the number
  const lastDigit = number % 10;

  // If the last digit is 4, 9, or 0, round up to the next multiple of 5
  if (lastDigit === 9 || lastDigit === 0 || lastDigit === 4) {
    return Math.ceil(number / 5) * 5;
  }

  // Otherwise, round down to the nearest multiple of 5
  return Math.floor(number / 5) * 5;
};

export const formatPercentageChange = (val) => {
  if (val > 0) {
    return `+${Math.floor(val)}%`;
  } else {
    return `${Math.floor(val)}%`;
  }
};

export const renderRightIconForSearchInput = ({ loading, query = '', setQuery }) => {
  if (loading) {
    return <ThreeDotsLoader color="#7357e9" style={{ height: '20px' }} />;
  } else if (query && query.length > 0) {
    return (
      <div
        className="cursor-pointer"
        onClick={() => {
          setQuery('');
        }}>
        <XIcon width={24} height={24} />
      </div>
    );
  }
  return <></>;
};

export const openFileInNewTab = (url, fileName = '') => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getExtraLabels = (labels) => {
  if (!labels) return '';
  const slicedVersion = labels.slice(1);
  return slicedVersion.map((label, i) => <div key={i}>{label}</div>);
};

export function getFirstTwoInitials(name) {
  return name
    ?.split(' ') // Split the name into words
    ?.slice(0, 2) // Get first two initials
    ?.map((word) => word[0].toUpperCase()) // Take the first letter of each word and capitalize it
    ?.join(''); // Join all initials into a single string
}

export function filterObject(obj, callback) {
  return Object.fromEntries(Object.entries(obj).filter(([key, value]) => callback(key, value)));
}

export const handleLogout = async () => {
  try {
    // Call backend API to invalidate session
    const response = await signOut();
    if (response?.status) {
      // Reset analytics on logout
      posthog?.reset();

      // Hide overlay container
      document.querySelector('.root-overlay-container').style.display = 'none';

      // Clear local storage items
      setStorageObject(isJsrStorageKey, { isJsrLogin: false });
      removeStorage('token');

      // Remove all feature keys
      Object.values(FeatureKeys).forEach((key) => {
        removeStorage(`feature_${key}`);
      });

      setStorageObject('new-rv-filter', {}, 'session');
      setStorageObject('onboarding-form-data', {});
      removeStorage(SWITCH_ID_KEY);

      const multibrandTokensKey = 'multiBrandTokens';

      const currentData = getStorageObject(multibrandTokensKey) || [];
      const currentTeamMemberLogged = getStorage(CURRENT_TEAMMEMBER_LOGGED_KEY);

      removeStorage(CURRENT_TEAMMEMBER_LOGGED_KEY);

      const updatedTokens = currentData.filter((token) => token.teamMemberId !== currentTeamMemberLogged);

      setStorage(multibrandTokensKey, JSON.stringify(updatedTokens));

      // Remove authentication cookies
      Cookies.remove('access_token');
      Cookies.remove('refresh_token');

      // Show success notification
      NotificationManager.success('Logged out!', '', 1000);

      // Reset third-party integrations
      Gleap.clearIdentity(); // Clear bug reporting tool identity
      Sentry.setUser(null); // Clear error tracking identity
      window._cio.reset(); // Reset customer.io tracking

      // Clear indexedDB cache
      clearAllCache();
    }
  } catch (error) {
    console.error('Error logging out:', error);
  } finally {
    // Reload page to ensure clean state
    window.location.reload();
  }
};
export const getCommandKey = () => {
  const platform = window.navigator.platform.toLowerCase();
  const isMac = platform.includes('mac');
  return isMac ? '⌘' : 'Ctrl';
};

export const getAltKey = () => {
  const platform = window.navigator.platform.toLowerCase();
  const isMac = platform.includes('mac');
  return isMac ? '⌥' : 'Alt';
};

export const isMac = () => {
  return navigator.userAgentData
    ? navigator.userAgentData.platform === 'macOS'
    : /Macintosh|MacIntel|MacPPC|Mac68K/i.test(navigator.userAgent);
};

export const LEVEL_UP_BELT_TYPES = {
  YELLOW: 'yellow',
  PURPLE: 'purple',
  BROWN: 'brown',
  BLACK: 'black',
};

export const LEVEL_UP_MODAL_CONTENT = {
  [LEVEL_UP_BELT_TYPES.YELLOW]: {
    title: 'Congrats, you went up a belt level!',
    description: (
      <p>
        You now know the basics of tactics and strategy
        <br />
        to build a solid foundation for your influencer
        <br />
        program. Keep watching the videos and
        <br />
        implementing to level up more!
      </p>
    ),
    image: yellowBelt,
  },
  [LEVEL_UP_BELT_TYPES.PURPLE]: {
    title: 'Nice! You got purple now!',
    description: (
      <p>
        You just got a purple belt in SARAL influencer
        <br />
        management mastery.
        <br />
        Next, try to onboard more creators or watch
        <br />
        more videos to keep levelling up!
      </p>
    ),
    image: purpleBelt,
  },
  [LEVEL_UP_BELT_TYPES.BROWN]: {
    title: (
      <p>
        Nearly a full master.
        <br />
        Congrats on earning your brown belt!
      </p>
    ),
    description: (
      <p>
        You’re scaling up your program and your
        <br />
        understanding of strategy now.
        <br />
        Continue building!
      </p>
    ),
    image: brownBelt,
  },
  [LEVEL_UP_BELT_TYPES.BLACK]: {
    title: 'We bow down to you, master!',
    description: (
      <p>
        You just earned your black belt on SARAL!
        <br />
        Congratulations on all the success.
        <br />
        You’re one of the few marketers who get to this level.
        <br />
        Take a screenshot, rejoice! We’ll send you some
        <br />
        special swag soon 😉
      </p>
    ),
    image: blackBelt,
  },
};

export const removeAtSymbol = (inputString) => {
  if (inputString?.startsWith('@')) {
    return inputString.substring(1);
  }
  return inputString;
};

export const validatePulseResponse = (response) => {
  if (isPulseEnabled()) {
    return response?.status && response?.status === API_STATUS.SUCCESS;
  } else {
    return response?.status;
  }
};